import * as THREE from 'three';

import {
  AISLE_WIDTH,
  BOOTH,
  type Fixture,
  LAYOUT_A,
  STAFF_DEFAULT,
  STAFF_REACH,
  type StaffSpot,
  WATCH_SPOT,
} from './config/booth';
import { createPlanControls } from './controls/plan';
import { createWalkControls } from './controls/walk';
import { buildBooth } from './scene/booth';
import { buildCrowd, buildStaff } from './scene/figures';
import { buildFixtureDims } from './scene/fixtureDims';
import { buildFixtures, type ScreenTextures } from './scene/fixtures';
import { buildHall } from './scene/hall';
import { buildLabels } from './scene/labels';
import { createStage } from './scene/renderer';
import { Simulation } from './sim/simulation';
import { createShirtPrint } from './textures/shirt';
import { buildPanel, type ViewMode } from './ui/panel';

const canvas = document.getElementById('scene') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui') as HTMLElement;

const stage = createStage(canvas);
const loader = new THREE.TextureLoader();
function appCapture(file: string): THREE.Texture {
  const t = loader.load(file);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}
// Real captures of the app; the LED wall mirrors the search screen.
const screens: ScreenTextures = {
  search: appCapture('/app-search.png'),
  'firearms-finder': appCapture('/app-firearms-finder.png'),
  'ammo-finder': appCapture('/app-ammo-finder.png'),
};
const shirtPrint = createShirtPrint();
const logo = new THREE.TextureLoader().load('/logo.png', (t) =>
  shirtPrint.paint(t.image as CanvasImageSource),
);
logo.colorSpace = THREE.SRGBColorSpace;
logo.anisotropy = 8;

let fixtures: Fixture[] = LAYOUT_A.map((f) => ({ ...f }));
let staff: StaffSpot[] = STAFF_DEFAULT.map((s) => ({ ...s }));

const sim = new Simulation({
  width: BOOTH.width,
  depth: BOOTH.depth,
  aisle: AISLE_WIDTH,
  fixtures,
  staff,
  staffReach: STAFF_REACH,
  watchSpot: WATCH_SPOT,
});

stage.scene.add(buildHall());
const booth = buildBooth(logo);
stage.scene.add(booth.group);
const fixtureSet = buildFixtures(fixtures, logo, screens);
stage.scene.add(fixtureSet.group);
const staffSet = buildStaff(staff, STAFF_REACH, shirtPrint.texture);
stage.scene.add(staffSet.group);
const crowd = buildCrowd();
stage.scene.add(crowd.group);
const labels = buildLabels();
const dims = buildFixtureDims(fixtures);
labels.add(dims.group);
labels.visible = false;
stage.scene.add(labels);

const walk = createWalkControls(canvas);
walk.setFixtures(fixtures);
const plan = createPlanControls(canvas);
plan.setDraggables([...fixtureSet.roots.values(), ...staffSet.roots.values()]);

let mode: ViewMode = 'walk';
function setMode(next: ViewMode): void {
  mode = next;
  walk.setEnabled(next === 'walk');
  plan.setEnabled(next === 'plan');
  labels.visible = next === 'plan';
  panel.setView(next);
}

/** Keep a dragged fixture inside the usable footprint. */
function clampFixture(f: Fixture, x: number, z: number): void {
  f.x = THREE.MathUtils.clamp(x, f.w / 2, BOOTH.width - f.w / 2);
  f.z = THREE.MathUtils.clamp(z, f.d / 2, BOOTH.depth - f.d / 2);
}

plan.onDrag = (t, x, z) => {
  if (t.kind === 'fixture') {
    const f = fixtures.find((fx) => fx.id === t.id);
    if (!f) return;
    clampFixture(f, x, z);
    fixtureSet.place(f);
    dims.place(f);
  } else {
    const s = staff.find((st) => st.id === t.id);
    if (!s) return;
    s.x = THREE.MathUtils.clamp(x, -AISLE_WIDTH, BOOTH.width - 0.5);
    s.z = THREE.MathUtils.clamp(z, 0.5, BOOTH.depth + AISLE_WIDTH);
    staffSet.place(s);
  }
};
/** The fixtures the sim and the walk collide with: the closet only while shown. */
let closetShown = true;
function activeFixtures(): Fixture[] {
  return closetShown ? fixtures : fixtures.filter((f) => f.kind !== 'closet');
}
function applyFixtures(): void {
  sim.setFixtures(activeFixtures());
  walk.setFixtures(activeFixtures());
}

plan.onDrop = (t) => {
  if (t.kind === 'fixture') {
    applyFixtures();
  } else {
    sim.setStaff(staff);
  }
};

const panel = buildPanel(uiRoot, {
  onView: setMode,
  onCrowd: (level) => {
    sim.crowd = level;
  },
  onHeight: (h) => booth.setHeight(h),
  onRuleZone: (on) => booth.setRuleZoneVisible(on),
  onReach: (on) => staffSet.setReachVisible(on),
  onShowStaff: (on) => {
    staffSet.group.visible = on;
  },
  onShowVisitors: (on) => {
    crowd.group.visible = on;
  },
  onShowCloset: (on) => {
    closetShown = on;
    for (const f of fixtures)
      if (f.kind === 'closet') {
        fixtureSet.roots.get(f.id)!.visible = on;
        dims.setVisible(f.id, on);
      }
    applyFixtures();
  },
  onShowSigns: (on) => fixtureSet.setSignsVisible(on),
  onReset: () => sim.reset(),
  onResetLayout: () => {
    fixtures = LAYOUT_A.map((f) => ({ ...f }));
    staff = STAFF_DEFAULT.map((s) => ({ ...s }));
    for (const f of fixtures) {
      fixtureSet.place(f);
      dims.place(f);
    }
    for (const s of staff) staffSet.place(s);
    applyFixtures();
    sim.setStaff(staff);
    walk.reset();
  },
});

const cameras = [walk.camera, plan.camera];
stage.resize(cameras);
window.addEventListener('resize', () => stage.resize(cameras));

const timer = new THREE.Timer();
let statTick = 0;
function frame(): void {
  timer.update();
  const dt = Math.min(timer.getDelta(), 0.05);
  sim.update(dt);
  crowd.sync(sim.agents, dt);
  walk.update(dt);
  statTick += dt;
  if (statTick > 0.25) {
    statTick = 0;
    panel.setStats(sim.stats);
  }
  stage.renderer.render(
    stage.scene,
    mode === 'walk' ? walk.camera : plan.camera,
  );
  requestAnimationFrame(frame);
}
setMode('walk');
frame();

// Dev console hook for checking views: boothSim.setPose(x, z, yaw, pitch).
if (import.meta.env.DEV) {
  (window as unknown as { boothSim: unknown }).boothSim = {
    setPose: walk.setPose,
    setMode,
  };
}
